import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  CircularProgress,
  alpha,
  LinearProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material';
import {
  AutoAwesome,
  ContentCopy,
  Refresh,
  CheckCircle,
  Psychology,
  TrendingUp,
  Edit,
  Save,
  Cancel,
  Check,
  Close as CloseIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppTheme } from '../../utils/themeUtils';

const AIAnalysisResults = ({
  analysisResult = '',
  analysisData = null,
  isAnalyzing = false,
  onAnalyze,
  onCopyAnalysis,
  selectedAnalysisTypes = [],
  confidence = null,
  type = 'snippet', // 'snippet' or other content types
  // Edit functionality props
  onEditTitle,
  onEditTags,
  onEditSummary,
  canEdit = false,
  // Apply/Dismiss functionality
  onApplyAll,
  onDismiss,
  showApplyDismiss = false
}) => {
  const { colors } = useAppTheme();
  const [copying, setCopying] = useState(false);
  
  // Edit dialog states
  const [editDialog, setEditDialog] = useState({ open: false, type: '', value: '' });
  const [editValue, setEditValue] = useState('');

  // Handle copy with feedback
  const handleCopy = async () => {
    if (!analysisResult.trim()) return;
    
    setCopying(true);
    try {
      await navigator.clipboard.writeText(analysisResult);
      if (onCopyAnalysis) {
        onCopyAnalysis();
      }
    } catch (error) {
      console.error('Failed to copy:', error);
    } finally {
      setTimeout(() => setCopying(false), 1000);
    }
  };

  // Handle edit dialog
  const handleEditOpen = (type, currentValue) => {
    setEditValue(Array.isArray(currentValue) ? currentValue.join(', ') : currentValue || '');
    setEditDialog({ open: true, type, value: currentValue });
  };

  const handleEditClose = () => {
    setEditDialog({ open: false, type: '', value: '' });
    setEditValue('');
  };

  const handleEditSave = () => {
    const { type } = editDialog;
    
    if (type === 'title' && onEditTitle) {
      onEditTitle(editValue.trim());
    } else if (type === 'tags' && onEditTags) {
      const tags = editValue.split(',').map(tag => tag.trim()).filter(tag => tag);
      onEditTags(tags);
    } else if (type === 'summary' && onEditSummary) {
      onEditSummary(editValue.trim());
    }
    
    handleEditClose();
  };

  // Get confidence color based on value
  const getConfidenceColor = (conf) => {
    if (conf >= 0.8) return colors.success || '#4caf50';
    if (conf >= 0.6) return colors.warning || '#ff9800';
    return colors.error || '#f44336';
  };

  // Format analysis result for better display
  const formatAnalysisResult = (result) => {
    if (!result) return '';
    
    // Split by double newlines to separate sections
    const sections = result.split('\n\n');
    return sections.map((section, index) => {
      // Check if section starts with **title**: format
      const titleMatch = section.match(/^\*\*(.*?):\*\*\n(.*)$/s);
      if (titleMatch) {
        return {
          id: index,
          title: titleMatch[1],
          content: titleMatch[2]
        };
      }
      return {
        id: index,
        title: null,
        content: section
      };
    });
  };

  const formattedSections = formatAnalysisResult(analysisResult);
  const title = 'AI Analysis';
  const emptyMessage = 'Click "Analyze" to get AI-powered insights and suggestions for your content.';

  if (!analysisResult && !isAnalyzing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)} 0%, ${alpha(colors.secondary, 0.05)} 100%)`,
            border: `1px solid ${alpha(colors.primary, 0.1)}`,
            textAlign: 'center'
          }}
        >
          <AutoAwesome 
            sx={{ 
              fontSize: 48, 
              color: alpha(colors.primary, 0.6),
              mb: 2 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.text,
              mb: 1,
              fontWeight: 'bold'
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.textSecondary,
              mb: 3,
              maxWidth: 400,
              mx: 'auto'
            }}
          >
            {emptyMessage}
          </Typography>
          
          {selectedAnalysisTypes.length > 0 && (
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" sx={{ mb: 2 }}>
              {selectedAnalysisTypes.map((type) => (
                <Chip
                  key={type}
                  label={type.replace('_', ' ').toUpperCase()}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: alpha(colors.primary, 0.3),
                    color: colors.primary
                  }}
                />
              ))}
            </Stack>
          )}
        </Paper>
      </motion.div>
    );
  }

  if (isAnalyzing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(colors.primary, 0.05)} 0%, ${alpha(colors.secondary, 0.05)} 100%)`,
            border: `1px solid ${alpha(colors.primary, 0.1)}`,
            textAlign: 'center'
          }}
        >
          <CircularProgress 
            size={48} 
            sx={{ 
              color: colors.primary,
              mb: 2 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.text,
              mb: 1,
              fontWeight: 'bold'
            }}
          >
            Analyzing Content...
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.textSecondary,
              mb: 3
            }}
          >
            AI is analyzing your content and generating insights.
          </Typography>
          
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 6,
              backgroundColor: alpha(colors.primary, 0.1),
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.primary,
                borderRadius: 2
              }
            }} 
          />
        </Paper>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            background: colors.surface,
            border: `1px solid ${alpha(colors.primary, 0.1)}`,
            overflow: 'hidden'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(colors.primary, 0.08)} 0%, ${alpha(colors.secondary, 0.08)} 100%)`,
              borderBottom: `1px solid ${alpha(colors.divider, 0.1)}`
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <Psychology sx={{ color: colors.primary, fontSize: 28 }} />
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: colors.text,
                      fontWeight: 'bold',
                      mb: 0.5
                    }}
                  >
                    {title}
                  </Typography>
                  {confidence && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TrendingUp 
                        sx={{ 
                          fontSize: 16, 
                          color: getConfidenceColor(confidence) 
                        }} 
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: getConfidenceColor(confidence),
                          fontWeight: 'bold'
                        }}
                      >
                        {Math.round(confidence * 100)}% Confidence
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" gap={1}>
                {/* Apply/Dismiss FAB Icons */}
                {showApplyDismiss && analysisData && (
                  <>
                    <Tooltip title="Apply all AI suggestions (title, tags, summary)">
                      <Fab
                        size="small"
                        onClick={onApplyAll}
                        sx={{
                          backgroundColor: colors.success || '#4caf50',
                          color: 'white',
                          width: 40,
                          height: 40,
                          '&:hover': {
                            backgroundColor: colors.success ? colors.success + 'dd' : '#45a049',
                            transform: 'scale(1.05)'
                          },
                          mr: 1,
                          boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Check />
                      </Fab>
                    </Tooltip>
                    
                    <Tooltip title="Dismiss AI suggestions">
                      <Fab
                        size="small"
                        onClick={onDismiss}
                        sx={{
                          backgroundColor: colors.error || '#f44336',
                          color: 'white',
                          width: 40,
                          height: 40,
                          '&:hover': {
                            backgroundColor: colors.error ? colors.error + 'dd' : '#d32f2f',
                            transform: 'scale(1.05)'
                          },
                          mr: 2,
                          boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <CloseIcon />
                      </Fab>
                    </Tooltip>
                  </>
                )}

                <Tooltip title="Copy Analysis">
                  <IconButton
                    onClick={handleCopy}
                    disabled={!analysisResult.trim() || copying}
                    sx={{
                      color: colors.primary,
                      '&:hover': {
                        backgroundColor: alpha(colors.primary, 0.1)
                      }
                    }}
                  >
                    {copying ? (
                      <CheckCircle sx={{ color: colors.success }} />
                    ) : (
                      <ContentCopy />
                    )}
                  </IconButton>
                </Tooltip>
                
                {onAnalyze && (
                  <Tooltip title="Regenerate Analysis">
                    <IconButton
                      onClick={onAnalyze}
                      disabled={isAnalyzing}
                      sx={{
                        color: colors.textSecondary,
                        '&:hover': {
                          backgroundColor: alpha(colors.primary, 0.1),
                          color: colors.primary
                        }
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {/* Display Title if available */}
            {analysisData?.titles && analysisData.titles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.text,
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1
                    }}
                  >
                    <AutoAwesome sx={{ color: colors.primary, fontSize: 24 }} />
                    {analysisData.titles[0]}
                  </Typography>
                  {canEdit && (
                    <Tooltip title="Edit Title">
                      <IconButton
                        size="small"
                        onClick={() => handleEditOpen('title', analysisData.titles[0])}
                        sx={{ color: colors.textSecondary }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Divider sx={{ borderColor: alpha(colors.primary, 0.2) }} />
              </Box>
            )}

            {/* Display Tags if available */}
            {analysisData?.tags && analysisData.tags.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.textSecondary,
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      letterSpacing: 1
                    }}
                  >
                    Tags
                  </Typography>
                  {canEdit && (
                    <Tooltip title="Edit Tags">
                      <IconButton
                        size="small"
                        onClick={() => handleEditOpen('tags', analysisData.tags)}
                        sx={{ color: colors.textSecondary }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {analysisData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: alpha(colors.primary, 0.3),
                        color: colors.primary,
                        backgroundColor: alpha(colors.primary, 0.05),
                        fontSize: '12px',
                        '&:hover': {
                          backgroundColor: alpha(colors.primary, 0.1)
                        }
                      }}
                    />
                  ))}
                </Stack>
                <Divider sx={{ mt: 2, borderColor: alpha(colors.divider, 0.1) }} />
              </Box>
            )}

            {/* Analysis Content with Edit Option */}
            <Box>
              {canEdit && (
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.textSecondary,
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      letterSpacing: 1
                    }}
                  >
                    Analysis Summary
                  </Typography>
                  <Tooltip title="Edit Summary">
                    <IconButton
                      size="small"
                      onClick={() => handleEditOpen('summary', analysisResult)}
                      sx={{ color: colors.textSecondary }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {formattedSections.length > 0 ? (
                <Stack spacing={3}>
                  {formattedSections.map((section) => (
                    <Box key={section.id}>
                      {section.title && (
                        <Typography
                          variant="h6"
                          sx={{
                            color: colors.primary,
                            fontWeight: 'bold',
                            mb: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              backgroundColor: colors.primary,
                              borderRadius: 2
                            }}
                          />
                          {section.title}
                        </Typography>
                      )}
                      
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-line',
                          fontSize: '15px'
                        }}
                      >
                        {section.content}
                      </Typography>
                      
                      {section.id < formattedSections.length - 1 && (
                        <Divider sx={{ mt: 2, borderColor: alpha(colors.divider, 0.1) }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    color: colors.text,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-line',
                    fontSize: '15px'
                  }}
                >
                  {analysisResult}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: colors.surface
          }
        }}
      >
        <DialogTitle sx={{ color: colors.text, fontWeight: 'bold' }}>
          Edit {editDialog.type === 'title' ? 'Title' : editDialog.type === 'tags' ? 'Tags' : 'Summary'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline={editDialog.type === 'summary'}
            rows={editDialog.type === 'summary' ? 6 : 1}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={
              editDialog.type === 'title' 
                ? 'Enter title...' 
                : editDialog.type === 'tags' 
                ? 'Enter tags separated by commas...'
                : 'Enter summary...'
            }
            sx={{ mt: 1 }}
          />
          {editDialog.type === 'tags' && (
            <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 1, display: 'block' }}>
              Separate multiple tags with commas
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} sx={{ color: colors.textSecondary }}>
            <Cancel sx={{ mr: 1 }} />
            Cancel
          </Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            sx={{ backgroundColor: colors.primary }}
          >
            <Save sx={{ mr: 1 }} />
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIAnalysisResults;
